import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { reportsAPI } from '../../../api';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { FormLabel } from '../../../components/ui/FormLabel';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/Card';
import { ArrowLeft } from 'lucide-react';

const currency = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n || 0);
const toInputDate = (d) => new Date(d).toISOString().split('T')[0];
const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(d);
};

/** Periodic refresh while the tab is open; pairs with refetchOnWindowFocus when returning to the app */
const PL_REFETCH_INTERVAL_MS = 60_000;
const PL_STALE_TIME_MS = 45_000;

/** Map GET /api/reports/pl body to accrual rows shown in the UI */
function mapPlResponseToAccrual(pl) {
  if (!pl) return null;
  const salesNet = parseFloat(pl.sales?.total) || 0;
  const salesVat = parseFloat(pl.sales?.vat) || 0;
  const purchasesNet = parseFloat(pl.costOfSales?.purchases) || 0;
  const purchasesVat = parseFloat(pl.costOfSales?.purchasesVat) || 0;
  return {
    salesNet,
    salesVat,
    salesGross: salesNet + salesVat,
    purchasesNet,
    purchasesVat,
    purchasesGross: purchasesNet + purchasesVat,
    grossProfit: parseFloat(pl.grossProfit) || 0,
    netProfit: parseFloat(pl.netProfit) || 0,
  };
}

export default function ProfitAndLoss() {
  const navigate = useNavigate();

  const today = useMemo(() => new Date(), []);
  const defaultStart = useMemo(() => new Date(today.getFullYear(), 0, 1), [today]);

  const initialRange = useMemo(
    () => ({ start_date: toInputDate(defaultStart), end_date: toInputDate(today) }),
    [defaultStart, today]
  );

  const [period, setPeriod] = useState(initialRange);
  /** Date range passed to the API; only changes on mount, Apply, or Clear so editing inputs does not refetch */
  const [activePeriod, setActivePeriod] = useState(initialRange);
  /**
   * Cash-basis toggle (plan §11): when true the server narrows the
   * cash-basis figures to payments that have been reconciled against
   * a bank statement line, giving a more accurate view of actual
   * cleared funds for statutory tax purposes.
   */
  const [reconciledOnly, setReconciledOnly] = useState(false);

  const {
    data: pl,
    isLoading: loading,
    error: queryError,
  } = useQuery(
    [
      'reports',
      'profit-loss',
      activePeriod.start_date,
      activePeriod.end_date,
      reconciledOnly,
    ],
    () =>
      reportsAPI.getProfitAndLoss({ ...activePeriod, reconciled_only: reconciledOnly }),
    {
      refetchInterval: PL_REFETCH_INTERVAL_MS,
      refetchOnWindowFocus: true,
      staleTime: PL_STALE_TIME_MS,
    }
  );

  const accrual = useMemo(() => mapPlResponseToAccrual(pl), [pl]);
  const error = queryError ? queryError.message || 'Failed to load Profit & Loss' : '';

  const handleApplyFilters = () => {
    setActivePeriod({ ...period });
  };

  const handleClearFilters = () => {
    setPeriod(initialRange);
    setActivePeriod(initialRange);
  };

  return (
    <Layout>
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/tenant/reports')}
                className="mb-2"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Reports
              </Button>
              <h1 className="text-display text-[var(--ds-text-primary)] font-bold">Profit & Loss</h1>
              <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">Financial performance summary</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 text-[var(--ds-status-danger)] px-4 py-3 rounded-[var(--ds-radius-md)] mb-4">{error}</div>
        )}

        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <FormLabel htmlFor="start-date">Start Date</FormLabel>
                <Input
                  id="start-date"
                  type="date"
                  value={period.start_date}
                  onChange={(e) => setPeriod((p) => ({ ...p, start_date: e.target.value }))}
                  className="h-8"
                />
              </div>
              <div>
                <FormLabel htmlFor="end-date">End Date</FormLabel>
                <Input
                  id="end-date"
                  type="date"
                  value={period.end_date}
                  onChange={(e) => setPeriod((p) => ({ ...p, end_date: e.target.value }))}
                  className="h-8"
                />
              </div>
              <div className="flex items-end">
                <Button variant="primary" size="sm" onClick={handleApplyFilters} className="w-full">Apply</Button>
              </div>
              <div className="flex items-end">
                <Button variant="outline" size="sm" onClick={handleClearFilters} className="w-full">Clear</Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--ds-accent-primary)]"></div>
              <p className="mt-2 text-xs text-[var(--ds-text-muted)]">Loading...</p>
            </div>
          ) : pl && accrual && (
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-h2 font-semibold mb-4 text-[var(--ds-text-primary)]">Accrual Basis</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs text-[var(--ds-text-primary)]">
                      <span>Sales (Net)</span>
                      <span className="tabular-nums">{currency(accrual.salesNet)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-[var(--ds-text-muted)]">
                      <span>VAT on Sales</span>
                      <span className="tabular-nums">{currency(accrual.salesVat)}</span>
                    </div>
                    <div className="flex justify-between text-xs font-medium text-[var(--ds-text-primary)]">
                      <span>Sales (Gross)</span>
                      <span className="tabular-nums">{currency(accrual.salesGross)}</span>
                    </div>
                    <hr className="my-2 border-[var(--ds-border-divider)]" />
                    <div className="flex justify-between text-xs text-[var(--ds-text-primary)]">
                      <span>Purchases (Net)</span>
                      <span className="tabular-nums">{currency(accrual.purchasesNet)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-[var(--ds-text-muted)]">
                      <span>VAT on Purchases</span>
                      <span className="tabular-nums">{currency(accrual.purchasesVat)}</span>
                    </div>
                    <div className="flex justify-between text-xs font-medium text-[var(--ds-text-primary)]">
                      <span>Purchases (Gross)</span>
                      <span className="tabular-nums">{currency(accrual.purchasesGross)}</span>
                    </div>
                    <hr className="my-2 border-[var(--ds-border-divider)]" />
                    <div className="flex justify-between text-xs font-semibold text-[var(--ds-text-primary)]">
                      <span>Gross Profit</span>
                      <span className="tabular-nums">{currency(accrual.grossProfit)}</span>
                    </div>
                    <div className="flex justify-between text-xs font-semibold text-[var(--ds-text-primary)]">
                      <span>Net Profit</span>
                      <span className="tabular-nums">{currency(accrual.netProfit)}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <h3 className="text-h2 font-semibold text-[var(--ds-text-primary)]">
                      Cash Basis
                    </h3>
                    <label className="flex items-center gap-2 text-[11px] text-[var(--ds-text-secondary)] select-none">
                      <input
                        type="checkbox"
                        checked={reconciledOnly}
                        onChange={(e) => setReconciledOnly(e.target.checked)}
                        className="h-3.5 w-3.5 rounded border-[var(--ds-border-strong)] text-[var(--ds-accent-primary)] focus:ring-[var(--ds-accent-primary)]"
                      />
                      Reconciled only
                    </label>
                  </div>
                  {reconciledOnly && (
                    <div className="-mt-2 mb-3 text-[11px] text-[var(--ds-text-muted)]">
                      Counting only payments matched to imported bank lines.
                    </div>
                  )}
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs text-[var(--ds-text-primary)]">
                      <span>Revenue (Cash Received)</span>
                      <span className="tabular-nums">{currency(pl.cashBasis?.revenueCash ?? 0)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-[var(--ds-text-primary)]">
                      <span>Expenses (Cash Paid)</span>
                      <span className="tabular-nums">{currency(pl.cashBasis?.expensesCash ?? 0)}</span>
                    </div>
                    <hr className="my-2 border-[var(--ds-border-divider)]" />
                    <div className="flex justify-between text-xs font-semibold text-[var(--ds-text-primary)]">
                      <span>Net Profit (Cash)</span>
                      <span className="tabular-nums">{currency(pl.cashBasis?.netProfit ?? 0)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {!loading && pl && accrual && (
          <Card className="mt-6 overflow-hidden">
            <CardHeader className="p-6 pb-2">
              <div>
                <CardTitle className="text-h2">Cash basis breakdown</CardTitle>
                <CardDescription>
                  Invoice receipts and purchase payments counted by payment date in this period
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent className="p-6 pt-2">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-sm font-semibold text-[var(--ds-text-primary)] mb-3">Revenue (cash received)</h4>
                  {(pl.cashBasis?.revenueBreakdown?.length ?? 0) === 0 ? (
                    <p className="text-xs text-[var(--ds-text-muted)]">No invoice payments in this period.</p>
                  ) : (
                    <div className="overflow-x-auto rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)]">
                      <table className="w-full text-xs text-left">
                        <thead>
                          <tr className="border-b border-[var(--ds-border-divider)] bg-[var(--ds-surface-panel2)]">
                            <th className="py-2 px-3 font-medium text-[var(--ds-text-secondary)]">Date</th>
                            <th className="py-2 px-3 font-medium text-[var(--ds-text-secondary)]">Invoice</th>
                            <th className="py-2 px-3 font-medium text-[var(--ds-text-secondary)]">Client</th>
                            <th className="py-2 px-3 font-medium text-[var(--ds-text-secondary)] text-right">Received</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(pl.cashBasis?.revenueBreakdown ?? []).map((row, idx) => (
                            <tr
                              key={`${row.invoiceNum}-${idx}`}
                              className="border-b border-[var(--ds-border-divider)] last:border-0 text-[var(--ds-text-primary)]"
                            >
                              <td className="py-2 px-3 tabular-nums whitespace-nowrap text-[var(--ds-text-secondary)]">{formatDate(row.paymentDate)}</td>
                              <td className="py-2 px-3 tabular-nums font-medium">{row.invoiceNum}</td>
                              <td className="py-2 px-3">{row.clientName}</td>
                              <td className="py-2 px-3 text-right tabular-nums">{currency(row.amountReceived)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-[var(--ds-text-primary)] mb-3">Expenses (cash paid)</h4>
                  {(pl.cashBasis?.expenseBreakdown?.length ?? 0) === 0 ? (
                    <p className="text-xs text-[var(--ds-text-muted)]">No purchase payments in this period.</p>
                  ) : (
                    <div className="overflow-x-auto rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)]">
                      <table className="w-full text-xs text-left">
                        <thead>
                          <tr className="border-b border-[var(--ds-border-divider)] bg-[var(--ds-surface-panel2)]">
                            <th className="py-2 px-3 font-medium text-[var(--ds-text-secondary)]">Date</th>
                            <th className="py-2 px-3 font-medium text-[var(--ds-text-secondary)]">Purchase</th>
                            <th className="py-2 px-3 font-medium text-[var(--ds-text-secondary)]">Supplier</th>
                            <th className="py-2 px-3 font-medium text-[var(--ds-text-secondary)] text-right">Paid</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(pl.cashBasis?.expenseBreakdown ?? []).map((row, idx) => (
                            <tr
                              key={`${row.purchaseNum}-${idx}`}
                              className="border-b border-[var(--ds-border-divider)] last:border-0 text-[var(--ds-text-primary)]"
                            >
                              <td className="py-2 px-3 tabular-nums whitespace-nowrap text-[var(--ds-text-secondary)]">{formatDate(row.paymentDate)}</td>
                              <td className="py-2 px-3 tabular-nums font-medium">{row.purchaseNum}</td>
                              <td className="py-2 px-3">{row.supplierName}</td>
                              <td className="py-2 px-3 text-right tabular-nums">{currency(row.amountPaid)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
